import React, { useContext, useEffect } from "react";
import { Gamepad2, Star, Trophy, Zap, ShieldCheck, MonitorSmartphone } from "lucide-react";
import { Link } from "react-router-dom";
import { DataContext } from "../../Context/DataContext";

const FeaturedSection = () => {
  const gameData = useContext(DataContext);

useEffect(() => {
  const loadAOS = async () => {
    const AOS = await import('aos');
    AOS.init({
      duration: 500,
      easing: "ease-out-cubic",
      once: true,
      offset: 100
    });
  };
  loadAOS();
}, []);

const createSlug = (text) => {
    return text
      ?.toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
};

  return (
    <section className="bg-gradient-to-b lg:px-8 from-[#0a0f1f] to-[#141a2e] sm:py-20 py-12 ">
      <div className="container mx-auto px-4">
        
        {/* 🚀 EXPANDED INTRO SECTION FOR SEO */}
        <div className="text-center mb-16" data-aos="fade-up" data-aos-delay="100">
          <h2 className="text-2xl sm:text-3xl md:text-4xl xl:text-5xl font-bold text-white mb-6">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-300">
              🔥 Trending & Popular
            </span>{" "}
            Games
          </h2>
          <div className="flex justify-center">
            <p className="text-gray-400 sm:text-lg text-[16px] md:w-4/5 lg:w-3/4 leading-relaxed font-light">
              Explore our hand-picked collection of the <strong>most popular free online games</strong> on the internet. 
              These top-rated HTML5 titles are loved by millions of players worldwide for their stunning graphics, responsive controls, and highly engaging gameplay loops. 
              From intense <strong>multiplayer action battles</strong> to relaxing <strong>logic puzzles</strong> and immersive role-playing adventures, 
              you can play these viral hits instantly on your mobile smartphone, tablet, or desktop PC <strong>without downloading anything</strong> or creating an account.
            </p>
          </div>
        </div>

        {/* GAME CARDS */}
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3 max-w-7xl mx-auto">
          {gameData?.slice(6, 9)?.map((game, index) => {

            const gameSlug = createSlug(game?.name);

            return(
              <div
              key={index}
              data-aos="zoom-in"
              data-aos-delay={`${300 + index * 100}`}
              className="relative bg-[#1b2236] rounded-3xl cursor-pointer overflow-hidden transition-all duration-500 group shadow-[0_0_20px_rgba(0,0,0,0.3)] hover:shadow-[0_0_35px_rgba(0,150,255,0.5)] hover:scale-105 hover:-translate-y-2 border border-white/5 hover:border-cyan-500/30"
            >
              <Link to={`/play/${gameSlug}`} state={{ game: game }} aria-label={`Play ${game?.name} free online`}>
                <div className="relative overflow-hidden rounded-t-3xl">
                  <picture>
                    <source srcSet={game?.image} type="image/webp" />
                    <img
                      src={game?.image}
                      alt={`Play ${game?.name} free online at Game Zone Hub`}
                      title={`Play ${game?.name}`}
                      className="w-full h-56 object-cover transform transition-transform duration-700 group-hover:scale-110"
                      loading="lazy"
                      decoding="async"
                      />
                  </picture>
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1b2236] via-transparent to-white/10 opacity-80 group-hover:opacity-100 transition-opacity duration-700"></div>
                  <div className="absolute top-4 right-4 bg-yellow-500/90 backdrop-blur-md text-black text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-lg">
                        <Star size={14} fill="black" /> Top Rated
                    </div>
                </div>
              </Link>

              <div className="p-6 relative z-10 flex flex-col h-[200px]">
                <h3
                  className="sm:text-xl text-lg font-bold text-white line-clamp-1 group-hover:text-cyan-300 transition-colors"
                  data-aos="fade-up"
                  data-aos-delay={`${400 + index * 150}`}
                >
                  {game?.name}
                </h3>
                
                <p
                    className="text-gray-400 text-sm mt-3 line-clamp-2 min-h-[40px] flex-grow leading-relaxed"
                    data-aos="fade-up"
                    data-aos-delay={`${450 + index * 150}`}
                  >
                    {game?.info || game?.desc || `Play ${game?.name} for free online. Enjoy one of the best browser games with no download required.`}
                </p>

                <Link 
                  to={`/play/${gameSlug}`} 
                  state={{ game: game }}
                  data-aos="flip-left"
                  data-aos-delay={`${500 + index * 150}`}
                  className="mt-5 px-6 py-3 bg-gradient-to-r from-cyan-500 via-blue-500 to-blue-600 text-white font-bold text-sm rounded-xl flex items-center justify-center gap-2 transition-all duration-300 shadow-lg hover:shadow-[0_0_20px_rgba(59,130,246,0.6)] w-full"
                  title={`Play ${game?.name} Now`}
                >
                  <Gamepad2 size={18} /> Play Online Free 
                </Link>
              </div>

              <div className="absolute -z-10 bottom-0 right-0 w-40 h-40 bg-cyan-600/20 rounded-full mix-blend-overlay blur-3xl group-hover:scale-150 transition-transform duration-1000 pointer-events-none"></div>
            </div>
            )
          })}
        </div>
        
        {/* 🚀 MASSIVELY EXPANDED EDITOR'S CHOICE SECTION FOR HIGH VALUE CONTENT */}
        <div className="sm:mt-20 mt-10 max-w-5xl mx-auto border-t border-white/10 pt-12" data-aos="fade-up">
            <div className="flex flex-col items-center text-center mb-8">
                <div className="flex items-center justify-center gap-2 mb-4 text-yellow-400 bg-yellow-500/10 px-6 py-2 rounded-full border border-yellow-500/20">
                    <Trophy size={22} /> <span className="text-sm font-extrabold tracking-widest uppercase">Editor's Choice & Weekly Highlights</span>
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4 mt-3">Why are these games trending?</h3>
                <p className="text-gray-400 text-base leading-relaxed max-w-3xl">
                    Our <strong>Featured Games</strong> section highlights the most played, highly-rated, and mechanically impressive titles currently available on Game Zone Hub. Our editorial team rigorously tests hundreds of browser games weekly to bring you only the absolute best.
                </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mt-8">
                <div className="bg-black/30 p-6 rounded-2xl border border-white/5">
                    <Zap className="text-blue-400 w-8 h-8 mb-4" />
                    <h4 className="text-lg font-bold text-white mb-2">Instant WebGL Performance</h4>
                    <p className="text-sm text-gray-400 leading-relaxed">These trending titles utilize advanced WebGL graphics APIs. This ensures that you experience console-quality 3D rendering and buttery-smooth 60 FPS gameplay natively within your web browser, bypassing the need for heavy local file processing.</p>
                </div>
                <div className="bg-black/30 p-6 rounded-2xl border border-white/5">
                    <MonitorSmartphone className="text-purple-400 w-8 h-8 mb-4" />
                    <h4 className="text-lg font-bold text-white mb-2">Seamless Cross-Play</h4>
                    <p className="text-sm text-gray-400 leading-relaxed">A core reason these games are viral is their unmatched accessibility. The interface and controls dynamically scale, meaning the experience is equally thrilling whether you are using a mechanical keyboard on a PC or touch-swipe controls on an iPhone or Android device.</p>
                </div>
                <div className="bg-black/30 p-6 rounded-2xl border border-white/5">
                    <ShieldCheck className="text-green-400 w-8 h-8 mb-4" />
                    <h4 className="text-lg font-bold text-white mb-2">Safe & Uninterrupted</h4>
                    <p className="text-sm text-gray-400 leading-relaxed">We guarantee a secure gaming environment. By playing these featured editor picks directly on Game Zone Hub, you avoid the risks of downloading unverified software. Enjoy endless, family-friendly entertainment in a highly secure, sandboxed environment.</p>
                </div>
            </div>
        </div>

      </div>
    </section>
  );
};

export default FeaturedSection;